import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  TextField,
  Button,
} from "@mui/material";

/* ===================== TYPE EXPEDIENTE ===================== */
import { actualizarExpediente2 } from "../services/expedientes.service";
import { Expediente } from "../types/expedientes";

/* ===================== INTERFACES ===================== */

interface Props {
  open: boolean;
  expediente: Expediente | null;
  onClose: () => void;
  onUpdate: (exp: Expediente) => void;
}

/* ===================== COMPONENTE ===================== */

const ExpedienteDetalleModal = ({ open, expediente, onClose, onUpdate }: Props) => {
  const [telefonoEdit, setTelefonoEdit] = useState("");
  const [obsEdit, setObsEdit] = useState("");
  const [saving, setSaving] = useState(false);

  // 🔥 FLAG PARA SABER SI ES PENAL
  const esPenal = expediente?.tipo_estado === "PE";

  /* ================== RESET ================== */

  useEffect(() => {
    if (open && expediente) {
      // 🔥 CARGAR DATA DEL EXPEDIENTE
      setTelefonoEdit(expediente.telefono || "");
      setObsEdit(expediente.observaciones || "");
    }
  }, [open, expediente?.id]);

  /* ================== GUARDAR ================== */

  const handleGuardar = async () => {
    if (!expediente) return;

    setSaving(true);

    try {
      const resp = await actualizarExpediente2(expediente.id, {
        telefono: telefonoEdit,
        observaciones: obsEdit,
      });

      // 🔥 actualizar grid sin recargar
      onUpdate(resp.data);

      // 🔥 cerrar modal
      onClose();

    } catch (err) {
      console.error(err);
      alert("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  /* ===================== RENDER ===================== */

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>

      {expediente ? (
        <>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between">
              <Typography fontWeight={600}>
                Detalle del Expediente
              </Typography>

              <Typography color="primary" fontWeight={600}>
                {expediente.nro_expediente}
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent dividers>

            {/* DATOS EXPEDIENTE */}

            <Stack spacing={1.5}>
              <Typography><b>Órgano:</b> {expediente.organo_jurisdiccional}</Typography>
              <Typography><b>Estado:</b> {expediente.estado}</Typography>
              <Typography><b>Tipo:</b> {expediente.tipo_expediente}</Typography>
              <Typography><b>Materia:</b> {expediente.materia}</Typography>
              <Typography>
                <b>Ingreso:</b>{" "}
                {new Date(expediente.fecha_ingreso).toLocaleDateString()}
              </Typography>
              <Typography><b>Demandante:</b> {expediente.demandante}</Typography>
              <Typography><b>Demandados:</b> {expediente.demandados}</Typography>

              {/* 🔥 CAMPOS ADICIONALES SOLO PARA PENAL */}
              {esPenal && expediente.sumilla && (
                <Typography><b>Sumilla:</b> {expediente.sumilla}</Typography>
              )}
              {esPenal && expediente.partes_procesales && (
                <Typography><b>Partes Procesales:</b> {expediente.partes_procesales}</Typography>
              )}
              {esPenal && expediente.tercero && (
                <Typography><b>Tercero:</b> {expediente.tercero}</Typography>
              )}
              {esPenal && expediente.monto != null && (
                <Typography><b>Monto:</b> S/ {expediente.monto.toLocaleString("es-PE", { minimumFractionDigits: 2 })}</Typography>
              )}
              {esPenal && expediente.dni && (
                <Typography><b>DNI:</b> {expediente.dni}</Typography>
              )}

              <TextField
                label="Teléfono"
                value={telefonoEdit}
                onChange={(e) => setTelefonoEdit(e.target.value)}
                fullWidth
              />

              <TextField
                label="Observaciones"
                value={obsEdit}
                onChange={(e) => setObsEdit(e.target.value)}
                fullWidth
                multiline
                rows={3}
              />

              <Button
                variant="contained"
                disabled={saving}
                onClick={handleGuardar}
              >
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </Stack>

          </DialogContent>
        </>
      ) : (
        <DialogContent dividers>
          <Typography>No hay expediente seleccionado</Typography>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default ExpedienteDetalleModal;